import type { CartItem } from "../context/CartContext";
import type { LocalOrderRewardSnapshot } from "./offline-sync/types";
import type { PaymentDialogOrderItem } from "../ui/PaymentDialog";

export type LoyaltyPreviewItem = {
  variantId: string;
  quantity: number;
  lineSubtotal: number;
  /** ภาษีของรายการ — ต้องรวมกับ lineSubtotal เป็นฐานคิดส่วนลดรางวัลสินค้าฟรี ไม่งั้น VAT จะค้าง */
  taxTotal?: number;
};

export function buildLoyaltyPreviewItemsFromCart(
  items: CartItem[],
  lineTotals: Array<{ lineSubtotal: number; taxTotal?: number }>,
): LoyaltyPreviewItem[] {
  return items.flatMap((item, index) => {
    if (!item.variantId) return [];
    const line = lineTotals[index];
    return [
      {
        variantId: item.variantId,
        quantity: item.quantity,
        lineSubtotal: line?.lineSubtotal ?? item.totalPrice,
        taxTotal: line?.taxTotal ?? 0,
      },
    ];
  });
}

/** Stable key for useEffect deps — ກັນ loop ເມື່ອ parent ສົ່ງ array reference ໃໝ່ທຸກ render */
export function loyaltyPreviewItemsKey(items: LoyaltyPreviewItem[]): string {
  if (items.length === 0) return "";
  return items
    .map(
      (item) =>
        `${item.variantId}:${item.quantity}:${Math.round(item.lineSubtotal * 100)}:${Math.round((item.taxTotal ?? 0) * 100)}`,
    )
    .join("|");
}

export function buildLoyaltyPreviewItemsFromPaymentOrderItems(
  orderItems?: PaymentDialogOrderItem[],
): LoyaltyPreviewItem[] {
  if (!orderItems?.length) return [];

  return orderItems.flatMap((item) => {
    if (!item.variantId) return [];
    return [
      {
        variantId: item.variantId,
        quantity: item.quantity ?? 1,
        lineSubtotal: item.lineSubtotal,
        taxTotal: item.taxTotal ?? 0,
      },
    ];
  });
}

type FreeProductRewardRef = Pick<
  LocalOrderRewardSnapshot,
  "fulfillmentType" | "productVariantId"
>;

/**
 * รายการที่แลก reward สินค้าฟรีแล้วไม่ควรได้คะแนนสะสม — ตัด 1 หน่วยจากบรรทัดแรกที่ variant ตรง
 * (สอดคล้อง applyFreeProductReward ที่เลือกรายการแรกที่ match variant)
 */
export function excludeFreeProductRewardFromLoyaltyPreviewItems<
  T extends LoyaltyPreviewItem,
>(items: T[], reward?: FreeProductRewardRef | null): T[] {
  if (!reward || reward.fulfillmentType !== "FREE_PRODUCT") {
    return items;
  }
  const variantId = reward.productVariantId?.trim();
  if (!variantId) return items;

  let rewardApplied = false;
  return items.flatMap((item) => {
    if (rewardApplied || item.variantId !== variantId) {
      return [item];
    }
    rewardApplied = true;
    if (item.quantity <= 1) {
      return [];
    }
    const remainingQty = item.quantity - 1;
    const subtotalRatio = remainingQty / item.quantity;
    const taxTotal = item.taxTotal ?? 0;
    return [
      {
        ...item,
        quantity: remainingQty,
        lineSubtotal:
          Math.round(item.lineSubtotal * subtotalRatio * 100) / 100,
        taxTotal: Math.round(taxTotal * subtotalRatio * 100) / 100,
      },
    ];
  });
}
